/**
 * Centralized utility for non-blocking PDF & Document exports across Atlas.
 * Prevents parent window thread freezes by decoupling window.opener and eliminating
 * blocking synchronous modal loops and auto-close race conditions.
 */

export const printToolbarStyles = `
  .atlas-print-toolbar {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    z-index: 99999;
    background-color: #0f172a;
    color: #f8fafc;
    padding: 10px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #334155;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }
  .atlas-print-toolbar-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .atlas-print-toolbar-title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #ffffff;
  }
  .atlas-print-toolbar-badge {
    background-color: rgba(37, 99, 235, 0.25);
    color: #93c5fd;
    border: 1px solid rgba(59, 130, 246, 0.4);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }
  .atlas-print-toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .atlas-btn-print {
    display: inline-flex;
    align-items: center;
    background-color: #2563eb;
    color: #ffffff;
    border: none;
    padding: 7px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-family: inherit;
  }
  .atlas-btn-print:hover {
    background-color: #1d4ed8;
  }
  .atlas-btn-close {
    display: inline-flex;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.1);
    color: #cbd5e1;
    border: 1px solid #475569;
    padding: 7px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;
  }
  .atlas-btn-close:hover {
    background-color: rgba(255, 255, 255, 0.18);
    color: #ffffff;
  }
  @media print {
    .atlas-print-toolbar {
      display: none !important;
    }
  }
`;

export function renderPrintToolbar(title = "Document Preview") {
  return `
    <div class="atlas-print-toolbar">
      <div class="atlas-print-toolbar-left">
        <span class="atlas-print-toolbar-title">${title}</span>
        <span class="atlas-print-toolbar-badge">Tip: In the print dialog, select "Save as PDF" to download as a file</span>
      </div>
      <div class="atlas-print-toolbar-right">
        <button type="button" class="atlas-btn-print" onclick="window.print()">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
          Print / Save as PDF
        </button>
        <button type="button" class="atlas-btn-close" onclick="window.close()">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 5px;"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          Close
        </button>
      </div>
    </div>
  `;
}

/**
 * Opens a document in a new tab without freezing the parent Atlas window.
 * Decouples opener references and avoids destructive auto-close timeouts.
 */
export function openDocumentPrintWindow({ title, headStyles = "", bodyHtml = "", options = {} }) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups to export the PDF.");
    return null;
  }

  const toolbarHtml = renderPrintToolbar(title);

  const fullHtml = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>${title || "Document Preview"}</title>
    <style>
      ${printToolbarStyles}
      ${headStyles}
    </style>
  </head>
  <body>
    ${toolbarHtml}
    ${bodyHtml}
  </body>
</html>`;

  try {
    printWindow.document.open();
    printWindow.document.write(fullHtml);
    printWindow.document.close();
    
    // Sever opener link to prevent cross-window thread locks where supported
    try {
      printWindow.opener = null;
    } catch (e) {
      // Ignore if browser restricts nulling opener
    }
  } catch (err) {
    console.error("Error writing document to print window:", err);
  }

  return printWindow;
}
